import React, { useState, useEffect } from "react";
import { FaSquarePollVertical, FaUserPen } from "react-icons/fa6";
import { HiOutlineThumbUp, HiOutlineThumbDown } from "react-icons/hi";
import { FaCalendarAlt, FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Card, Button, Row, Col, Form, Modal, Dropdown } from "react-bootstrap";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";
import { FileX, ThumbsUp, ThumbsDown, ImageOff } from "lucide-react";
import "./AdminPage.css";
import NotificationBell from "../NotificationBell/NotificationBell";
import socket from "../../socket";
import ManageAssistants from "../ManageAssistants/ManageAssistants";
import LeaveManagement from "../AdminDashboard/LeaveManagement";


const NoImageIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='none' stroke='%23c4b5fd' stroke-width='1.5'%3E%3Crect x='3' y='3' width='18' height='18' rx='3'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='m21 15-5-5L5 21'/%3E%3C/svg%3E";

const AdminPage = () => {
  const navigate = useNavigate();
  const { isAdmin, adminCategory, user, isAssistant } = useAuth();
  const [assistantCategories, setAssistantCategories] = useState([]);


  const [complaints, setComplaints] = useState([]);
  const [sortOption, setSortOption] = useState("default");

  // CHANGE #1: default status filter is now "Active" (Pending + Ongoing)
  // instead of "All", so Resolved complaints are hidden on first load.
  const [statusFilter, setStatusFilter] = useState("Active");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;

  const DEFAULT_IMAGE = NoImageIcon;

  const [userVotes, setUserVotes] = useState({});

  const [expandedCard, setExpandedCard] = useState(null);

  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);

  // Assignment State
  const [assistants, setAssistants] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedComplaintId, setSelectedComplaintId] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState("");
  const [isReassign, setIsReassign] = useState(false);
  const [currentComplaint, setCurrentComplaint] = useState(null);

  // Workload Detail Modal State
  const [showWorkloadModal, setShowWorkloadModal] = useState(false);
  const [workloadComplaints, setWorkloadComplaints] = useState([]);
  const [workloadTitle, setWorkloadTitle] = useState("");
  const [workloadLoading, setWorkloadLoading] = useState(false);
  const [workloadCategoriesHint, setWorkloadCategoriesHint] = useState([]);
  const [showLeaveManagement, setShowLeaveManagement] = useState(false);

  const handleWorkloadClick = async (assistant, statusType) => {
    setWorkloadTitle(`${assistant.name || assistant.email} - ${statusType} Complaints`);
    setShowWorkloadModal(true);
    setWorkloadLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      let url = `${baseUrl}/admin-api/assistant-complaints?email=${assistant.email}`;
      if (statusType === "Pending") url += `&status=Pending`;
      else if (statusType === "Solved") url += `&status=Resolved`;

      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      let data = res.data.complaints || [];
      if (statusType === "Ongoing/Reopened") {
        data = data.filter(c => c.status === "Ongoing" || c.status === "Reopened");
      }

      // Calculate what other categories are taking up their time before filtering
      const categoriesPresent = [...new Set(data.map(c => c.category))].filter(Boolean);
      setWorkloadCategoriesHint(categoriesPresent.filter(cat => cat !== currentComplaint?.category));

      // Keep only current category filter correctly!
      if (currentComplaint?.category) {
        data = data.filter(c => c.category === currentComplaint.category);
      }
      setWorkloadComplaints(data);
    } catch (e) {
      console.error("Failed to fetch workload complaints:", e);
    } finally {
      setWorkloadLoading(false);
    }
  };

  const fetchAssistants = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${baseUrl}/admin-api/all-assistants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const assistantsWithWorkload = (res.data.assistants || []).map(ast => ({
        ...ast,
        activeComplaints: ast.activeComplaints || 0,
        pendingCount: ast.pendingCount || 0,
        ongoingCount: ast.ongoingCount || 0,
        resolvedCount: ast.resolvedCount || 0,
        reopenedCount: ast.reopenedCount || 0
      }));
      console.log("[AdminPage] Assistants fetched count:", assistantsWithWorkload.length, assistantsWithWorkload.map(a => ({ email: a.email, categories: a.categories, category: a.category, name: a.name })));
      setAssistants(assistantsWithWorkload);
    } catch (error) {
      console.error("[AdminPage] Error fetching assistants:", error.response?.status, error.response?.data || error.message);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchAssistants();
    }
  }, [isAdmin]);

  const getAssistantsForCategory = (category) => {
    if (!category) {
      return [];
    }

    const normalizedCategory = category.trim().toLowerCase();
    const matched = assistants.filter(ast => {
      let catMatch = false;
      if (ast.categories && Array.isArray(ast.categories)) {
        catMatch = ast.categories.some(cat => cat && cat.trim().toLowerCase() === normalizedCategory);
      }
      if (!catMatch && ast.category && typeof ast.category === "string") {
        catMatch = ast.category.trim().toLowerCase() === normalizedCategory;
      }
      return catMatch;
    });
    if (matched.length === 0 && assistants.length > 0) {
      console.log(`[AdminPage] getAssistantsForCategory("${category}") => 0 matches out of ${assistants.length} assistants`, assistants.map(a => ({ email: a.email, categories: a.categories, category: a.category })));
    }
    return matched;
  };

  const handleTakeBack = async (complaintId) => {
    try {
      const token = localStorage.getItem("authToken");

      await axios.put(
        `${baseUrl}/admin-api/take-back-complaint`,
        {
          complaintId,
          adminEmail: user.email
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert("Complaint taken back successfully. You can now resolve it.");

      fetchComplaints(selectedCategory, statusFilter);

    } catch (error) {
      console.error("Take back failed:", error);
      alert("Failed to take back complaint");
    }
  };

  const fetchAssistantCategories = async () => {
    try {
      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        `${baseUrl}/admin-api/assistant-categories?email=${user.email.toLowerCase()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setAssistantCategories(res.data.categories || []);
    } catch (err) {
      console.error("Error fetching assistant categories:", err);
    }
  };
  useEffect(() => {
    if (isAssistant) {
      fetchAssistantCategories();
    }
  }, [isAssistant]);

  const openAssignModal = (complaintId, complaint = null, reassign = false) => {
    setSelectedComplaintId(complaintId);
    setCurrentComplaint(complaint);
    setSelectedAssistant("");
    setIsReassign(reassign);
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    if (!selectedAssistant) return;
    try {
      const token = localStorage.getItem("authToken");
      // const endpoint = isReassign 
      //   ? `${baseUrl}/admin-api/reassign-complaint`
      //   : `${baseUrl}/admin-api/assign-complaint`;

      const endpoint = `${baseUrl}/admin-api/assign-complaint`;

      await axios.post(endpoint, {
        complaintId: selectedComplaintId,
        assistantEmail: selectedAssistant,
        adminEmail: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      socket.emit("complaintAssigned", {
        assistantEmail: selectedAssistant
      });

      setShowAssignModal(false);
      const assistantData = assistants.find(a => a.email === selectedAssistant);
      const assistantName = assistantData?.name || selectedAssistant;
      alert(`Complaint ${isReassign ? 're' : ''}assigned to ${assistantName}`);
      fetchComplaints(selectedCategory, statusFilter); // Refresh list
    } catch (error) {
      console.error("Assignment failed:", error);
      alert(`Failed to ${isReassign ? 're' : ''}assign complaint`);
    }
  };

 const handleVote = (complaintId, voteType) => {
    const complaint = complaints.find(
        (c) => c.complaint_id === complaintId
    );

    if (!complaint) return;

    const previousVote = userVotes[complaintId];

    let likes = Number(complaint.likes) || 0;
    let dislikes = Number(complaint.dislikes) || 0;

    // Click the same button again to remove the vote
    if (previousVote === voteType) {
        if (voteType === "upvote") {
            likes = Math.max(0, likes - 1);
        } else {
            dislikes = Math.max(0, dislikes - 1);
        }

        setUserVotes((prev) => {
            const updated = { ...prev };
            delete updated[complaintId];
            return updated;
        });
    } else {
        // Remove the previous vote if switching
        if (previousVote === "upvote") {
            likes = Math.max(0, likes - 1);
        } else if (previousVote === "downvote") {
            dislikes = Math.max(0, dislikes - 1);
        }

        // Apply the new vote
        if (voteType === "upvote") {
            likes++;
        } else {
            dislikes++;
        }

        setUserVotes((prev) => ({
            ...prev,
            [complaintId]: voteType,
        }));
    }

    // Update the complaint cards
    setComplaints((prev) =>
        prev.map((c) =>
            c.complaint_id === complaintId
                ? { ...c, likes, dislikes }
                : c
        )
    );

    // Update the View More popup if it is open
    setExpandedCard((prev) =>
        prev && prev.complaint_id === complaintId
            ? { ...prev, likes, dislikes }
            : prev
    );
};



  // Normalize admin categories into array
  const adminCats = Array.isArray(adminCategory)
    ? adminCategory
    : [adminCategory].filter(Boolean);

  // Combine admin, assistant, and currently visible complaints' categories so nothing is missed
  const fetchedComplaintCats = complaints.map(c => c.category).filter(Boolean);
  const allPossibleCategories = [...new Set([...adminCats, ...assistantCategories, ...fetchedComplaintCats])];

  const ALLOWED_ADMIN_CATEGORIES = [
    "Infrastructure",
    "Canteen",
    "Examination",
    "Academics",
    "Fee Payments and Accounts",
    "Hostel Food",
    "EduPrime",
    "Extracurricular and Events",
    "Security",
    "Sports",
    "Housekeeping",
    "Audio-Visual Equipment",
    "Parking",
    "Transport",
    "Library",
    "IT and Networking",
    "Others",
  ];

  // Filter out removed categories (Boys Hostel / Girls Hostel) and enforce allowed order
  const categories = allPossibleCategories
    .filter(cat => cat && ALLOWED_ADMIN_CATEGORIES.includes(cat))
    .sort((a, b) => ALLOWED_ADMIN_CATEGORIES.indexOf(a) - ALLOWED_ADMIN_CATEGORIES.indexOf(b));

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [assignmentFilter, setAssignmentFilter] = useState("All"); // All, Assigned, Unassigned, AssignedByMe

  useEffect(() => {
    if (isAdmin || isAssistant) { // ✅ Fetch if Admin OR Assistant
      fetchComplaints(selectedCategory, statusFilter, assignmentFilter);
    }
  }, [isAdmin, isAssistant, selectedCategory, statusFilter, assignmentFilter, sortOption]);

  const fetchComplaints = async (category, status, assignmentStatus = "All") => {
    setLoading(true);
    setError(false);

    try {
      const token = localStorage.getItem("authToken");
      const userEmail = user?.email?.toLowerCase();
     

      let response;

      if (isAdmin && !isAssistant) {
        const params = new URLSearchParams();
        if (category && category !== "All") params.append("category", category);
        if (status && status !== "All" && status !== "Active") params.append("status", status);
        if (assignmentStatus && assignmentStatus !== "All") params.append("assignmentFilter", assignmentStatus);

        
        response = await axios.get(
          `${baseUrl}/admin-api/filter-complaints?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (isAssistant) {
        console.log("[AdminPage] Assistant fetch URL:", `${baseUrl}/admin-api/assistant-complaints?email=${userEmail}`);
        response = await axios.get(
          `${baseUrl}/admin-api/assistant-complaints?email=${userEmail}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        console.log("[AdminPage] Not authorized for complaints fetch");
        setComplaints([]);
        setLoading(false);
        return;
      }

      let complaintsData = response?.data?.complaints ?? [];
     

      // ✅ Apply frontend filtering for assistants
      if (isAssistant) {
        if (category && category !== "All") {
          complaintsData = complaintsData.filter(
            (c) => c.category === category
          );
        }

        if (status && status !== "All" && status !== "Active") {
          complaintsData = complaintsData.filter(
            (c) => c.status === status
          );
        }
      }

      setComplaints(complaintsData);
      setLoading(false);
    } catch (err) {
      console.error("[AdminPage] Error fetching complaints:", err);
      setError(true);
      setLoading(false);
    }
  };

  const handleSort = (option) => setSortOption(option);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) {
    return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
   }
   if (days < 365) {
    return `${months} ${months === 1 ? "month" : "months"} ago`;
   }
   return `${years} ${years === 1 ? "year" : "years"} ago`;
  };
  const toggleExpand = (complaint) => {
    setExpandedCard(complaint);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return <span className="status-pill status-pending">Pending</span>;
      case "Ongoing":
        return <span className="status-pill status-ongoing">Ongoing</span>;
      case "Resolved":
        return <span className="status-pill status-resolved">✅ Resolved</span>;
      case "Reopened":
        return <span className="status-pill status-reopened">🔄 Reopened</span>;
      default:
        return null;
    }
  };

  // CHANGE #5: single, central place that (a) hides Resolved complaints when
  // "Active" is selected, and (b) enforces Pending -> Ongoing/Reopened -> Resolved
  // priority ordering. This uses a stable sort on priority only, so the
  // Latest/Popular/Disagreed ordering already applied in fetchComplaints is
  // preserved within each status group. Filtering/sorting happens here,
  // before .map(), instead of inline in the JSX.
  const displayedComplaints = React.useMemo(() => {
    let filtered = [...complaints];

    if (statusFilter === "Active") {
      filtered = filtered.filter(
        (c) => c.status === "Pending" || c.status === "Ongoing" || c.status === "Reopened"
      );
    } else if (statusFilter !== "All") {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    const statusPriority = { Pending: 0, Ongoing: 1, Reopened: 1, Resolved: 2 };
    filtered.sort(
      (a, b) => (statusPriority[a.status] ?? 3) - (statusPriority[b.status] ?? 3)
    );

    return filtered;
  }, [complaints, statusFilter]);

  return (
    <div className="container adminpage">
      <div className="text-center mt-3 mb-4">
        <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
          <p className="adminheading mb-0">
            {isAssistant ? "My Assigned Complaints" : "Admin Requests Dashboard"}

           
          </p>

          <NotificationBell
            baseUrl={baseUrl}
            adminCategory={adminCategory}
            user={user}
            isAssistant={isAssistant}
          />
        </div>

        <div className="filter-controls-wrapper mt-4 d-flex align-items-center gap-3">
          {/* Category dropdown if multiple categories */}

          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ maxWidth: "250px" }}
          >
            <option value="All">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Form.Select>

          {/* <Form.Select
            className="me-3"
            value={sortOption}
            onChange={(e) => handleSort(e.target.value)}
            style={{ minWidth: "165px" }}
          >
            <option value="default">Sort by: Latest</option>
            <option value="most-liked">Popular</option>
            <option value="most-disliked">Disagreed</option>
          </Form.Select> */}

          {/* CHANGE #4: Status dropdown now includes "Active" as the default
              option, so admins can explicitly choose Active / Pending / Ongoing /
              Resolved / All. */}
          <Form.Select
            className="me-3"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ maxWidth: "160px" }}
          >
            <option value="Active">Active</option>
            <option value="Pending">Pending</option>
            <option value="Ongoing">Ongoing</option>
            <option value="Resolved">Resolved</option>
            <option value="All">All Status</option>
          </Form.Select>

          {!isAssistant && (() => {
            const getAssignmentFilterLabel = () => {
              if (assignmentFilter === "All") return "All Complaints";
              if (assignmentFilter === "Assigned") return "Assigned Complaints";
              if (assignmentFilter === "Unassigned") return "Unassigned";
              const ast = assistants.find(a => a.email === assignmentFilter);
              return ast?.name ? ast.name : assignmentFilter;
            };

            const uniqueAssistantsForMenu = (() => {
              const catsToCheck = selectedCategory !== "All" ? [selectedCategory] : categories;
              const filtered = assistants.filter(ast =>
                catsToCheck.some(cat =>
                  ast.categories?.some(c => c.toLowerCase() === cat.toLowerCase()) ||
                  ast.category?.toLowerCase() === cat.toLowerCase()
                )
              );
              const unique = [];
              const seen = new Set();
              for (const ast of filtered) {
                if (!seen.has(ast.email)) {
                  seen.add(ast.email);
                  unique.push(ast);
                }
              }
              return unique;
            })();

            return (
              <Dropdown onSelect={(e) => setAssignmentFilter(e)}>
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="filter-dropdown-toggle me-3 py-2 text-start"
                  style={{ minWidth: "190px", backgroundColor: "#fff", borderColor: "#cfd7ff", color: "#333", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  {getAssignmentFilterLabel()}
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow-sm border-0" style={{ borderRadius: '10px' }}>
                  <Dropdown.Item eventKey="All">All Complaints</Dropdown.Item>
                  <Dropdown.Item eventKey="Assigned">Assigned Complaints</Dropdown.Item>
                  <Dropdown.Item eventKey="Unassigned">Unassigned</Dropdown.Item>

                  {uniqueAssistantsForMenu.length > 0 && (
                    <div className="nested-dropdown mt-2">
                      <Dropdown.Divider />
                      <div className="nested-dropdown-trigger px-3 py-1 text-dark d-flex justify-content-between align-items-center" style={{ cursor: 'default', fontWeight: 'bold' }}>
                        Filter by Member <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>▶</span>
                      </div>
                      <div className="nested-dropdown-menu shadow rounded border-0 bg-white">
                        {uniqueAssistantsForMenu.map(ast => (
                          <Dropdown.Item key={ast.email} eventKey={ast.email} className="py-2 px-3">
                            {ast.name ? ast.name : ast.email}
                          </Dropdown.Item>
                        ))}
                      </div>
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            );
          })()}

          {!isAssistant && (
            <Button
              variant="outline-primary"
              onClick={() => navigate("/admin-analysis")}
              className="analysis-btn d-flex align-items-center no-hover-bg"
            >
              <FaSquarePollVertical size={20} color="#1e90ff" className="me-2" />
              Analysis
            </Button>
          )}
          {!isAssistant && (
            <Button
              variant="outline-primary"
              onClick={() => navigate("/manage-assistants", { state: { from: "/adminpage" } })}
              className="analysis-btn d-flex align-items-center no-hover-bg"
            >
              Manage Assistants
            </Button>
          )}
          {!isAssistant && (
            <Button
              variant="outline-primary"
              onClick={() => setShowLeaveManagement(true)}
              className="analysis-btn d-flex align-items-center no-hover-bg"
            >
              Leave
            </Button>
          )}


        </div>
      </div>

      {loading ? (
        <div className="loading-wrapper">
          <div className="spinner" />
          <p className="text-muted mt-3">Loading requests...</p>
        </div>
      ) : displayedComplaints.length === 0 ? (
        <div className="text-center text-muted mt-5 mb-5 fs-5 d-flex flex-column align-items-center">
          <FileX size={64} className="iconn mb-3 text-secondary" />
          <h5 className="text-dark fw-semibold">No requests available</h5>
          {selectedCategory && statusFilter === "All" && (
            <p>
              No requests have been submitted in{" "}
              <strong>{selectedCategory}</strong> category yet.
            </p>
          )}
          {selectedCategory && statusFilter !== "All" && (
            <p>
              No requests in <strong>{selectedCategory}</strong> with{" "}
              <strong>{statusFilter}</strong> status.
            </p>
          )}
        </div>
      ) : (
        <Row className="gx-4 gy-4">

          {displayedComplaints.map((complaint) => (
            <Col key={complaint.complaint_id} sm={12} md={6} lg={4}>
              <Card
                className={`card-hover-effect glass-effect rounded-4 custom-card-container w-100 d-flex flex-column ${expandedCard === complaint.complaint_id ? "expanded-card" : ""
                  }`}
              >
                <div className="complaint-image-wrapper">
                  {complaint.image ? (
                    <img
                      src={complaint.image}
                      alt="Complaint"
                      className="complaint-image"
                      onClick={() => {
                        setModalImageSrc(complaint.image);
                        setShowImageModal(true);
                      }}
                    />
                  ) : (
                    <div className="no-image-placeholder">
                      <ImageOff size={30} strokeWidth={1.6} />
                      <span>No Image Available</span>
                    </div>
                  )}

                  <div
                    className="status-overlay position-absolute"
                    style={{ top: "10px", left: "10px", zIndex: 3 }}
                  >
                    {getStatusBadge(complaint.status)}
                  </div>
                </div>

                <div
                  className="card-body-content"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#c4b5fd transparent",
                  }}
                >
                  <div className="complaint-date-row">
                    <span className="date-icon-wrap">
                      <FaCalendarAlt />
                    </span>
                    {formatDate(complaint.timestamp)}

                    <div className="d-flex align-items-center gap-2 ms-auto">
                      {complaint.flagged?.isFlagged && complaint.status !== "Resolved" && (
                        <span className="flagged-badge">Flagged</span>
                      )}
                      <Button
                        className="custom-pencil-button"
                        onClick={() =>
                          navigate(`/complaints-details/${complaint.complaint_id}`)
                        }
                      >
                        <FaEdit size={28} className="edit-icon-purple" style={{ cursor: "pointer" }} />
                      </Button>
                    </div>
                  </div>

                  <Card.Title className="card-title-text">
                    {complaint.title}
                  </Card.Title>

                  <div style={{ flex: 1 }}>
                    <Card.Text
                      className="card-desc-text mb-2"
                    >
                      {complaint.description.length > 200
                        ? `${complaint.description.substring(0, 200)}...`
                        : complaint.description}

                      {complaint.description.length > 200 && (
                        <span
                          className="view-more-link ms-2"
                          onClick={() => setExpandedCard(complaint)}
                          style={{
                            color: "#007bff",
                            cursor: "pointer",
                            fontWeight: 500,
                          }}
                        >
                          View More
                        </span>
                      )}
                    </Card.Text>
                  </div>

                  {(() => {
                    const cat = (complaint.category || "").toString().toLowerCase();
                    const isIt = cat.includes("it") && cat.includes("network");
                    // const itLocation = complaint.it_details?.location;
                    const itConnection = complaint.it_details?.connectionType;
                    const itRoom = complaint.it_details?.room_number || complaint.room_number;
                    const itSpeed = complaint.it_details?.internet_speed || complaint.internet_speed;
                    const itDuration = complaint.it_details?.issue_duration || complaint.issue_duration;
                    const itMobile = complaint.it_details?.mobile_number || complaint.mobile_number;
                    const itName = complaint.it_details?.name || complaint.name;
                    const itDepartment = complaint.it_details?.department || complaint.department;
                    if (isIt && (itDepartment || itName || itConnection || itRoom || itSpeed || itDuration || itMobile)) {
                      return (
                        <div className="it-summary-admin mb-2" style={{ color: "#495057", fontSize: "0.95rem" }}>
                          {itConnection && (
                          <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 8 }}>🔗</span>
                          <small><strong>Connection:</strong>&nbsp;{itConnection}</small>
                          </div>
                )}
                          {itDepartment && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>🏢</span><small><strong>Department:</strong>&nbsp;{itDepartment}</small></div>}
                          {itName && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>👤</span><small><strong>Name:</strong>&nbsp;{itName}</small></div>}
                          {itRoom && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>🏷️</span><small><strong>Room:</strong>&nbsp;{itRoom}</small></div>}
                          {itSpeed && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>📶</span><small><strong>Internet Speed:</strong>&nbsp;{itSpeed}</small></div>}
                          {itDuration && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>⏱️</span><small><strong>Duration:</strong>&nbsp;{itDuration}</small></div>}
                          {itMobile && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>📞</span><small><strong>Mobile:</strong>&nbsp;{itMobile}</small></div>}
                        </div>
                      );
                    }
                    return null;
                  })()}

                  <div className="mt-2 mb-2">
                    {isAssistant && complaint.assignedBy ? (
                      <small className="text-muted d-block">
                        <strong>Assigned By:</strong> {complaint.assignedBy}
                      </small>
                    ) : null}

                    {(isAdmin && !isAssistant) && getAssistantsForCategory(complaint.category).length > 0 && (
                      
                      complaint.assignedAssistant ? (
                        <div className="mt-1">
                          <small className="text-muted d-block">
                            <strong>Assigned to:</strong>
                          </small>
                          <small className="text-muted d-block">
                            {complaint.assignedAssistant}
                          </small>

                          {complaint.status === "Pending" && (
                            <div className="d-flex gap-2 mt-2 w-100">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                className="reassignfun"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAssignModal(complaint.complaint_id, complaint, true);
                                }}
                              >
                                Reassign
                              </Button>

                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="takebackfun"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm("Are you sure you want to take back this complaint? You can resolve it after taking back.\n\nPress OK to continue or Cancel to abort.")) {
                                    handleTakeBack(complaint.complaint_id);
                                  }
                                }}
                              >
                                Take Back
                              </Button>
                            </div>
                          )}
                        </div>

                      ) : (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mt-1"
                          onClick={(e) => { e.stopPropagation(); openAssignModal(complaint.complaint_id, complaint, false); }}
                        >
                          Assign to Team
                        </Button>
                      )
                    )}

                    {complaint.assistantRemarks && (
                      <div className="mt-2 px-2 py-1 rounded" style={{ backgroundColor: "#f3fdf5", borderLeft: "3px solid #28a745" }}>
                        <small className="d-block text-dark" style={{ fontSize: "0.8rem", lineHeight: "1.4" }}>
                          <strong className="text-success">Last Update: </strong>
                          {
                            (() => {
                              let remark = complaint.assistantRemarks || "";
                              remark = remark.replace(/by assistant/gi, "").replace(/\s+/g, " ").trim();
                              remark = remark.replace(/by\s+by/gi, "by");
                              return remark;
                            })()
                          }
                          {complaint.lastUpdatedBy && !complaint.assistantRemarks.includes(complaint.lastUpdatedBy) && (
                            <span className="text-muted fw-semibold ps-1">
                              {`by ${complaint.lastUpdatedBy}`}
                            </span>
                          )}
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="mt-auto d-flex justify-content-between align-items-center pt-3">
                    <div className="d-flex align-items-center gap-3">
                      <button
                        className={`vote-btn vote-btn-up ${userVotes[complaint.complaint_id] === "upvote" ? "active" : ""}`}
                        onClick={() => handleVote(complaint.complaint_id, "upvote")}
                      >
                        <ThumbsUp size={18} />
                        {complaint.likes}
                      </button>
                      <button
                        className={`vote-btn vote-btn-down ${userVotes[complaint.complaint_id] === "downvote" ? "active" : ""}`}
                        onClick={() => handleVote(complaint.complaint_id, "downvote")}
                      >
                        <ThumbsDown size={18} />
                        {complaint.dislikes}
                      </button>
                    </div>
                    <span className="category-tag">{complaint.category}</span>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

      )}

      {/* 🔥 VIEW MORE POPUP */}
      {expandedCard && (
        <div className="overlay" onClick={() => setExpandedCard(null)}>
          <Card
            className="popup-card p-4 rounded-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              className="close-btn-inside-modalh"
              onClick={() => setExpandedCard(null)}
            >
              ✕
            </button>

            {/* Status */}
            <div className="mb-2">
              {getStatusBadge(expandedCard.status)}
            </div>

            {/* Date */}
            <Card.Text className="text-primary fw-semibold">
              <FaCalendarAlt className="me-2" />
              {formatDate(expandedCard.timestamp)}
            </Card.Text>

            {/* Title */}
            <Card.Title className="fs-4 fw-bold mt-3">
              {expandedCard.title}
            </Card.Title>

            {/* Description */}
            <Card.Text className="mt-3 text-secondary">
              {expandedCard.description}
            </Card.Text>

            <div className="mt-auto d-flex w-100 align-items-center pt-2 px-0">
              <span
                className="category-tag px-2 py-1 rounded-pill me-auto"
                style={{ fontSize: "0.8rem" }}
              >
                {expandedCard.category}
              </span>

              <div className="d-flex align-items-center gap-3 ms-auto">
                <button
                  className={`btnscolor d-flex align-items-center gap-1 px-2 py-1 rounded-pill shadow-sm border-0 ${userVotes[expandedCard.complaint_id] === "upvote"
                    ? "bg-success text-white"
                    : "text-success"
                    }`}
                  onClick={() => handleVote(expandedCard.complaint_id, "upvote")}
                  style={{ fontSize: "1rem" }}
                >
                  <ThumbsUp size={20} />
                  {expandedCard.likes}
                </button>

                <button
                  className={`btnscolor d-flex align-items-center gap-1 px-2 py-1 rounded-pill shadow-sm border-0 ${userVotes[expandedCard.complaint_id] === "downvote"
                    ? "bg-danger text-white"
                    : "text-danger"
                    }`}
                  onClick={() => handleVote(expandedCard.complaint_id, "downvote")}
                  style={{ fontSize: "1rem" }}
                >
                  <ThumbsDown size={20} />
                  {expandedCard.dislikes}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Assignment Modal */}
      <Modal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        centered
        dialogClassName="assign-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isReassign ? "Reassign Complaint" : "Assign Complaint"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="assign-form-group">
            <Form.Label>Select Member</Form.Label>
            <Form.Text className="d-block text-muted mb-2 small">
              Select the member to {isReassign ? "reassign" : "assign"} this complaint to
            </Form.Text>

            {currentComplaint?.assignedAssistant && isReassign && (
              <div className="alert alert-info mb-3 small">
                <strong>Currently assigned to:</strong> {currentComplaint.assignedAssistant}
              </div>
            )}

            <Form.Select
              className="w-100"
              value={selectedAssistant}
              onChange={(e) => setSelectedAssistant(e.target.value)}
            >
              <option value="" disabled>
                Select Member
              </option>

              {/* Filter by category and sort assistants by workload (lighter load first) */}
              {[...getAssistantsForCategory(currentComplaint?.category)]
                .sort((a, b) => (a.activeComplaints || 0) - (b.activeComplaints || 0))
                .map(ast => (
                  <option key={ast.email} value={ast.email}>
                    {ast.name && ast.name !== ast.email
                      ? `${ast.name} (${ast.email})`
                      : ast.email
                    }
                    {" – "}
                    {ast.pendingCount || 0} pending, {ast.ongoingCount || 0} ongoing, {ast.resolvedCount || 0} solved
                  </option>

                ))}
            </Form.Select>

            {/* Show workload summary for this category */}
            {getAssistantsForCategory(currentComplaint?.category).length > 0 && (
              <div className="mt-3 p-2 bg-light rounded small">
                <strong className="d-block mb-2">📊 Workload Summary ({currentComplaint?.category}):</strong>
                <div style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
                  {[...getAssistantsForCategory(currentComplaint?.category)]
                    .sort((a, b) => (a.activeComplaints || 0) - (b.activeComplaints || 0))
                    .map(ast => (
                      <div key={ast.email} className="mb-2 p-2 border-bottom">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong>{ast.name || ast.email}:</strong>
                          <span className="badge bg-light text-dark" style={{ border: '1px solid #ddd' }}>
                            {ast.activeComplaints || 0} Active
                          </span>
                        </div>
                        <div className="d-flex flex-wrap gap-2 text-muted" style={{ fontSize: '0.8rem' }}>
                          <span
                            className="badge bg-white text-warning border border-warning"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleWorkloadClick(ast, 'Pending')}
                          >
                            ⏳ Pending: <strong>{ast.pendingCount || 0}</strong>
                          </span>
                          <span
                            className="badge bg-white text-primary border border-primary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleWorkloadClick(ast, 'Ongoing/Reopened')}
                          >
                            🚀 Ongoing/Reopened: <strong>{(ast.ongoingCount || 0) + (ast.reopenedCount || 0)}</strong>
                          </span>
                          <span
                            className="badge bg-white text-success border border-success"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleWorkloadClick(ast, 'Solved')}
                          >
                            ✅ Solved: <strong>{ast.resolvedCount || 0}</strong>
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
                <small className="text-muted d-block mt-2">💡 Tip: Choose member with fewer active complaints for better distribution</small>
              </div>
            )}

            {/* Show message if no assistants for this category */}
            {getAssistantsForCategory(currentComplaint?.category).length === 0 && (
              <div className="alert alert-warning mt-3 small">
                ⚠️ No assistants assigned to the <strong>{currentComplaint?.category}</strong> category yet.
              </div>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="assign-footer">
          <Button
            variant="secondary"
            onClick={() => setShowAssignModal(false)}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!selectedAssistant}
          >
            {isReassign ? "Reassign Complaint" : "Assign Complaint"}
          </Button>
        </Modal.Footer>
      </Modal>

      

<Modal show={showWorkloadModal} onHide={() => setShowWorkloadModal(false)} centered size="lg" dialogClassName="workload-modal">
  <Modal.Header closeButton className="border-bottom pb-3">
    <Modal.Title className="fw-bold text-dark w-100 text-center fs-4">{workloadTitle}</Modal.Title>
  </Modal.Header>
  <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
    {workloadLoading ? (
      <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
    ) : workloadComplaints.length === 0 ? (
      <div className="text-center py-4">
        <p className="text-muted m-0">
          No complaints found for this status in <strong>{currentComplaint?.category}</strong>.
        </p>
        {workloadCategoriesHint.length > 0 && (
          <small className="text-muted mt-2 d-block">
            These active complaints are from: {workloadCategoriesHint.join(", ")}
          </small>
        )}
      </div>
    ) : (
      <div className="list-group border-0">
        {workloadComplaints.map(c => (
          <div 
            key={c.complaint_id} 
            className="list-group-item list-group-item-action flex-column align-items-start mb-3 p-3 shadow-sm workload-item"
            style={{ borderRadius: '12px', border: '1px solid #e0e5f0', cursor: 'pointer', transition: 'all 0.2s ease-in-out', backgroundColor: '#fff' }}
            onClick={() => {
                setShowWorkloadModal(false);
                navigate(`/complaints-details/${c.complaint_id || c._id}`);
            }}
          >
            <div className="d-flex w-100 justify-content-between mb-2">
              <h6 className="mb-0 fw-bold fs-5" style={{ color: '#0056b3' }}>{c.title}</h6>
              <small className="text-secondary fw-semibold">{new Date(c.timestamp).toLocaleDateString()}</small>
            </div>
            <p className="mb-3 text-secondary" style={{ fontSize: '0.95rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {c.description}
            </p>
            <small className="text-dark d-flex align-items-center">
              <strong>Status:</strong> <span className="ms-1 me-2 text-muted">{c.status}</span> <span className="mx-2 text-muted">|</span> 
              <strong>Complaint ID:</strong> <span className="ms-1" style={{ fontFamily: 'monospace', color: '#495057' }}>{(c.complaint_id || c._id || '').toString().slice(-6).toUpperCase()}</span>
            </small>
          </div>
        ))}
        {workloadCategoriesHint.length > 0 && (
          <div className="text-center mt-2 p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px border #e9ecef' }}>
            <small className="text-muted">
              <strong>Note:</strong> The remaining active complaints are from: <strong>{workloadCategoriesHint.join(", ")}</strong>
            </small>
          </div>
        )}
      </div>
    )}
  </Modal.Body>
  <Modal.Footer className="border-top pt-3 justify-content-center">
    <Button variant="secondary" className="px-5 py-2 rounded-pill fw-bold" onClick={() => setShowWorkloadModal(false)} style={{ backgroundColor: '#6c757d', border: 'none' }}>Close</Button>
  </Modal.Footer>
</Modal>

      {!isAssistant && (
        <LeaveManagement
          show={showLeaveManagement}
          onHide={() => setShowLeaveManagement(false)}
        />
      )}

    </div>
  );
};

export default AdminPage;